import {
  BusinessTwoTone,
  CheckCircleOutline,
  CloudSyncOutlined,
  CloudSyncTwoTone,
  CreditCardTwoTone,
  ScienceTwoTone,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Paper,
  Step,
  StepConnector,
  StepIconProps,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AvanzuHeader } from '../../components/AvanzuHeader';

const steps = [
  {
    label: 'Organisation Details',
    path: '/onboarding/organisation',
    icon: BusinessTwoTone,
  },
  {
    label: 'Create first project',
    path: '/onboarding/project',
    icon: ScienceTwoTone,
  },
  {
    label: 'Connect Xero',
    path: '/onboarding/xero',
    icon: CloudSyncTwoTone,
  },
  {
    label: 'Select Plan',
    path: '/onboarding/subscription',
    icon: CreditCardTwoTone,
  },
];

function StringOfPearlsStepIcon(props: StepIconProps) {
  const { active, completed, className, icon } = props;

  const icons: { [index: string]: React.ElementType } = {
    1: BusinessTwoTone,
    2: ScienceTwoTone,
    3: CloudSyncTwoTone,
    4: CreditCardTwoTone,
  };

  const IconComponent = icons[String(icon)];

  return (
    <Box
      className={`string-of-pearls-icon ${active ? 'active' : ''} ${completed ? 'completed' : ''} ${className || ''}`}
    >
      {completed ? (
        <CheckCircleOutline sx={{ fontSize: '2.5rem' }} />
      ) : (
        <IconComponent sx={{ fontSize: '2rem' }} />
      )}
    </Box>
  );
}

const OnboardingWrapper: React.FC = () => {
  const { organisationId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const currentStepIndex = steps.findIndex((step) => step.path === location.pathname);
  const activeStep = currentStepIndex === -1 ? 0 : currentStepIndex;

  const handleStepClick = (index: number) => {
    // Only allow navigating to completed steps or the next step
    if (index <= activeStep + 1) {
      navigate(steps[index].path);
    }
  };

  if (!organisationId) {
    console.error(
      'No organisation ID found during onboarding. Redirecting to organisation selection.'
    );
    navigate('/select-organisation');
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        {/* Logo/Brand */}
        <Box sx={{ textAlign: 'center', mb: 4, mx: 'auto' }}>
          <Box sx={{ display: 'inline-block' }}>
            <AvanzuHeader />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            R&D Tax Incentive Management
          </Typography>
        </Box>

        {/* String of Pearls Stepper */}
        <Box sx={{ mb: 5 }}>
          <Stepper
            alternativeLabel
            activeStep={activeStep}
            connector={<StepConnector />}
            className="string-of-pearls"
          >
            {steps.map((step, index) => (
              <Step
                key={step.label}
                sx={{ cursor: index <= activeStep + 1 ? 'pointer' : 'default' }}
                onClick={() => handleStepClick(index)}
              >
                <StepLabel StepIconComponent={StringOfPearlsStepIcon}>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Main Content Card */}
        <Paper sx={{ p: 4 }}>
          <Outlet />
        </Paper>

        {/* Help Text */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Need help? Contact us at{' '}
            <Box
              component="a"
              href="mailto:support@avanzu.com"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              support@avanzu.com
            </Box>{' '}
            Skip onboarding and head straight to your{' '}
            <Box
              component="a"
              href="/dashboard"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              dashboard
            </Box>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default OnboardingWrapper;
